/* istanbul ignore file */
import { Box, Table, SystemStyleObject } from '@chakra-ui/react';
import { useContext, useMemo } from 'react';
import { ColumnStyleDefinition, DataTableContext, TableCell } from '..';
import { TableRowActions } from './table-row-actions';
const getStyleOverride = (styles: ColumnStyleDefinition | undefined) => {
  return styles ? { ...styles.common, ...styles.cell } : {};
};

export const TableRow = <RowType extends object>(props: { row: RowType; index: number }) => {
  const { tableProps } = useContext(DataTableContext);
  const { row, index } = props;
  const {
    visibleKeys,
    styles,
    showVisibleFieldsToggle,
    rowActions,
    centerAlignedKeys,
    rightAlignedKeys,
    bulkActions,
    verticalBordersProp,
    paddingCell,
    variant,
    stickyLeftColumnKeys,
    stickyRightColumnKeys,
    highlightedRows,
    onRowClick,
    customTableRowActions,
    columnStyleOverride,
  } = tableProps;

  const { generalCellStyle, cellStylePerColumn } = columnStyleOverride || {};
  const generalStyleOverride = getStyleOverride(generalCellStyle);

  const rowItem = (columnKey: string, appliedStyle: SystemStyleObject) => {
    const borderRightStyle = { ':not(:last-child)': { 'border-right': verticalBordersProp } };

    const isCenterAligned = centerAlignedKeys?.includes(columnKey);
    const isRightAligned = rightAlignedKeys?.includes(columnKey);

    const columnStyleOverride = getStyleOverride(cellStylePerColumn?.[columnKey]);

    return (
      <Table.Cell
        key={columnKey}
        css={{ ...borderRightStyle, ...generalStyleOverride, ...appliedStyle, ...columnStyleOverride }}
        textAlign={isCenterAligned ? 'center' : isRightAligned ? 'right' : 'left'}
        onClick={onRowClick ? onRowClick.bind(this, row) : undefined}
        {...(paddingCell ? { padding: paddingCell } : {})}
        style={{
          color: 'var(--chakra-colors-typography-base)',
          lineHeight: '1.15rem',
          padding: '16px 12px',
        }}
      >
        <TableCell fieldKey={columnKey} row={row} index={index} />
      </Table.Cell>
    );
  };

  const rowStyle = useMemo(() => {
    if (variant !== 'fixed-column') {
      return styles.tableRow;
    }

    const rowHighlight = highlightedRows?.includes((row as { itemBatchId: string }).itemBatchId)
      ? styles.tableRowHighlightSolid
      : {};

    return { ...styles.tableRowSolid, ...rowHighlight };
  }, [highlightedRows, row, styles.tableRow, styles.tableRowHighlightSolid, styles.tableRowSolid, variant]);

  return (
    <Table.Row
      key={`datatable-${index}`}
      css={rowStyle}
      _hover={{ backgroundColor: 'var(--chakra-colors-backgrounds-gray)' }}
    >
      {bulkActions && bulkActions.length > 0 && (
        <Table.Cell css={styles.bulkSelectCell} key={`bulkActionItem${index}`}>
          {/* <Checkbox isChecked={isRowSelected} onChange={handleCheckboxClick} /> */}
        </Table.Cell>
      )}
      {/* Columns listed on the left defined in `stickyLeftColumnKeys` */}
      {stickyLeftColumnKeys?.map((leftColumnKey) => {
        /* Since Chakra UI has limitations what pseudo classes can be used we had to be creative to create sticky columns in the table */
        const appliedStyle =
          variant === 'fixed-column' ? { ...styles.dataRows, ...styles.columnFixedFirst } : styles.dataRows;
        return rowItem(leftColumnKey, appliedStyle);
      })}
      {visibleKeys?.map((columnKey) => {
        /* Since Chakra UI has limitations what pseudo classes can be used we had to be creative to create sticky columns in the table */
        const appliedStyle = variant === 'fixed-column' ? { minWidth: '150px', ...styles.dataRows } : styles.dataRows;
        return rowItem(columnKey, appliedStyle);
      })}
      {/* Columns listed on the right defined in `stickyRightColumnKeys` */}
      {stickyRightColumnKeys?.map((rightColumnKey) => {
        const appliedStyle =
          variant === 'fixed-column' ? { ...styles.dataRows, ...styles.columnFixedLast } : styles.dataRows;
        return rowItem(rightColumnKey, appliedStyle);
      })}
      {(showVisibleFieldsToggle || rowActions) && (
        <Table.Cell
          css={
            variant === 'fixed-column'
              ? {
                  ...styles.tableMenuDropdownCell,
                  ...styles.tableMenuDropdownCellFixed,
                }
              : styles.tableMenuDropdownCell
          }
          key={`rowAction${index}`}
        >
          {rowActions && !customTableRowActions && <TableRowActions row={row} />}
          {customTableRowActions && (
            <Box display="flex" justifyContent="center">
              {customTableRowActions({ row })}
            </Box>
          )}
        </Table.Cell>
      )}
    </Table.Row>
  );
};
